<template>
  <div class="retention-realtime">
    <retention-tabs />

    <div class="page-header">
      <div class="header-text">
        <h2>Realtime Parameter Builder</h2>
        <p class="hint">
          Assemble the <code>realtimeRetentionMessage</code> backend parameter from real catalog products, offers and
          approved messages, then copy the JSON into the parameter (nothing here is saved automatically).
        </p>
      </div>
    </div>

    <div v-if="isLoading" class="empty">Loading catalog &amp; messages…</div>

    <template v-else>
      <div class="import-box">
        <button class="panel-toggle" @click="importOpen = !importOpen">
          <i :class="importOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
          Load existing JSON
        </button>
        <div v-if="importOpen" class="import-body">
          <textarea
            v-model="importText"
            class="import-textarea"
            spellcheck="false"
            placeholder='Paste a current realtimeRetentionMessage value, e.g. { "com.app.weekly.t1": { "messageIdentifier": "..." } }'
          ></textarea>
          <div class="import-actions">
            <button class="btn btn-primary" @click="loadImport">Load</button>
            <button class="btn btn-secondary" @click="importText = ''; importError = ''">Clear</button>
            <span v-if="importError" class="import-error">{{ importError }}</span>
          </div>
        </div>
      </div>

      <div class="bulk-box">
        <button class="panel-toggle" @click="bulkOpen = !bulkOpen">
          <i :class="bulkOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
          Apply one message to multiple products
        </button>
        <div v-if="bulkOpen" class="bulk-body">
          <p class="bulk-intro">
            Configure a message shape once, pick the products to show it on, and add them all as rules in one go.
          </p>
          <realtime-rule-card
            :rule="draft"
            :products="products"
            :approved-messages="approvedMessages"
            :groups="groups"
            :members-by-group-id="membersByGroupId"
            :used-product-ids="[]"
            :hide-product="true"
            @input="draft = $event"
          />
          <div class="bulk-assign">
            <div class="bulk-pick-head">
              <label class="field-label">Apply to products</label>
              <div class="bulk-pick-tools">
                <div class="bulk-search">
                  <i class="fas fa-search"></i>
                  <input v-model="productSearch" type="text" placeholder="Filter products…" />
                  <i v-if="productSearch" class="fas fa-times clear" @click="productSearch = ''"></i>
                </div>
                <button class="link-btn" @click="selectAllVisible">Select all</button>
                <button class="link-btn" @click="selectNone">None</button>
                <span class="bulk-selcount">{{ bulkSelected.size }} selected</span>
              </div>
            </div>
            <div class="product-checklist">
              <label
                v-for="p in bulkVisibleProducts"
                :key="p.productId"
                class="product-check"
                :title="p.productId"
                :class="{ disabled: disabledProductIds.has(p.productId), checked: bulkSelected.has(p.productId) }"
              >
                <input
                  type="checkbox"
                  :checked="bulkSelected.has(p.productId)"
                  :disabled="disabledProductIds.has(p.productId)"
                  @change="toggleProduct(p.productId)"
                />
                <span class="pc-id">{{ p.productId }}</span>
                <span v-if="usedSet.has(p.productId)" class="pc-tag">has rule</span>
                <span v-else-if="draft.mode === 'SWITCH_PLAN' && draft.targetProductId === p.productId" class="pc-tag">= target</span>
              </label>
              <p v-if="bulkVisibleProducts.length === 0" class="bulk-empty">No products match.</p>
            </div>
            <div class="bulk-actions">
              <button class="btn btn-primary" :disabled="!bulkReady" @click="applyBulk">
                <i class="fas fa-layer-group"></i>
                Add to {{ bulkSelected.size }} product{{ bulkSelected.size === 1 ? '' : 's' }}
              </button>
              <span v-if="!bulkDraftValid" class="bulk-hint">{{ bulkDraftHint }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="builder">
        <div class="rules-col">
          <div v-if="rules.length === 0" class="empty">
            No product rules yet. Click <strong>+ Add product rule</strong> to start.
          </div>

          <realtime-rule-card
            v-for="(r, i) in rules"
            :key="r.key"
            :rule="r"
            :products="products"
            :approved-messages="approvedMessages"
            :groups="groups"
            :members-by-group-id="membersByGroupId"
            :used-product-ids="usedProductIds"
            @input="updateRule(i, $event)"
            @remove="removeRule(i)"
          />

          <button class="btn btn-add" @click="addRule">
            <i class="fas fa-plus"></i> Add product rule
          </button>

          <p v-if="hasUnresolved" class="unresolved-note">
            <i class="fas fa-exclamation-triangle"></i>
            Some imported messages aren't among this app's approved messages. Their identifiers are preserved as-is, but verify them.
          </p>
        </div>

        <div class="json-pane">
          <div class="json-head">
            <span class="json-title">
              JSON
              <span class="json-count">{{ completeCount }} / {{ rules.length }} rule{{ rules.length === 1 ? '' : 's' }}</span>
            </span>
            <button class="btn btn-primary btn-copy" :disabled="completeCount === 0" @click="copyJson">
              <i class="fas fa-copy"></i> Copy
            </button>
          </div>
          <pre class="json-body">{{ jsonText }}</pre>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import RetentionTabs from './Tabs.vue';
import RealtimeRuleCard from '../../components/Retention/RealtimeRuleCard.vue';
import notify from '../../util/notification';
import { makeRule, messageIdentifierValue, modeFieldsValid, ruleComplete } from '../../components/Retention/realtimeRule';

export default {
  name: 'RetentionRealtime',
  components: { RetentionTabs, RealtimeRuleCard },
  data() {
    return {
      rules: [],
      importOpen: false,
      importText: '',
      importError: '',
      bulkOpen: false,
      draft: makeRule(),
      bulkSelected: new Set(),
      productSearch: ''
    };
  },
  computed: {
    products() {
      // Legacy (hand-entered) catalog: offers here carry price/source, which
      // the store-synced mobile catalog behind productIds does not.
      return this.$store.getters['retention/realtimeProducts'] || [];
    },
    approvedMessages() {
      return this.$store.getters['retention/defaultEligibleMessages'] || [];
    },
    groups() {
      return this.$store.getters['retention/groups'] || [];
    },
    // groupId -> approved members with a language (the servable set).
    membersByGroupId() {
      const map = {};
      for (const m of this.approvedMessages) {
        if (!m.groupId || !m.language) continue;
        (map[m.groupId] = map[m.groupId] || []).push(m);
      }
      return map;
    },
    isLoading() {
      return this.$store.getters['retention/isLoadingMessages'];
    },
    usedProductIds() {
      return this.rules.map((r) => r.productId).filter(Boolean);
    },
    builtJson() {
      const out = {};
      for (const rule of this.rules) {
        if (!ruleComplete(rule)) continue;
        const value = { messageIdentifier: messageIdentifierValue(rule.message) };
        if (rule.mode !== 'REGULAR') value.targetProductId = rule.targetProductId;
        if (rule.mode === 'PROMOTIONAL_OFFER') value.offerIdentifier = rule.offerIdentifier;
        out[rule.productId] = value;
      }
      return out;
    },
    jsonText() {
      return JSON.stringify(this.builtJson, null, 2);
    },
    completeCount() {
      return this.rules.filter(ruleComplete).length;
    },
    hasUnresolved() {
      return this.rules.some((r) => {
        const m = r.message;
        if (!m) return false;
        if (m.localized) return m.entries.some((e) => e.message && e.message.__unresolved);
        return m.single && m.single.__unresolved;
      });
    },
    bulkDraftValid() {
      return !!messageIdentifierValue(this.draft.message) && modeFieldsValid(this.draft);
    },
    bulkReady() {
      return this.bulkDraftValid && this.bulkSelected.size > 0;
    },
    usedSet() {
      return new Set(this.usedProductIds);
    },
    disabledProductIds() {
      // Already has a rule, or (Switch Plan only) equals the draft's target.
      const s = new Set(this.usedSet);
      if (this.draft.mode === 'SWITCH_PLAN' && this.draft.targetProductId) s.add(this.draft.targetProductId);
      return s;
    },
    bulkVisibleProducts() {
      const q = this.productSearch.trim().toLowerCase();
      if (!q) return this.products;
      return this.products.filter((p) => p.productId.toLowerCase().includes(q));
    },
    bulkDraftHint() {
      const need = ['a message'];
      if (this.draft.mode !== 'REGULAR') need.push('a target product');
      if (this.draft.mode === 'PROMOTIONAL_OFFER') need.push('an offer');
      return `Configure ${need.join(', ')} above first.`;
    }
  },
  watch: {
    usedProductIds() { this.pruneDisabled(); },
    'draft.mode'() { this.pruneDisabled(); },
    'draft.targetProductId'() { this.pruneDisabled(); }
  },
  async mounted() {
    await Promise.all([
      this.$store.dispatch('retention/fetchRealtimeProducts'),
      this.$store.dispatch('retention/fetchMessages'),
      this.$store.dispatch('retention/fetchGroups')
    ]);
  },
  methods: {
    addRule() {
      this.rules.push(makeRule());
    },
    cloneMessage(m) {
      // Deep clone so each generated rule edits its own message state.
      return JSON.parse(JSON.stringify(m || { localized: false, single: null, entries: [] }));
    },
    toggleProduct(pid) {
      if (this.disabledProductIds.has(pid)) return;
      const next = new Set(this.bulkSelected);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      this.bulkSelected = next;
    },
    selectAllVisible() {
      const next = new Set(this.bulkSelected);
      for (const p of this.bulkVisibleProducts) {
        if (!this.disabledProductIds.has(p.productId)) next.add(p.productId);
      }
      this.bulkSelected = next;
    },
    selectNone() {
      this.bulkSelected = new Set();
    },
    pruneDisabled() {
      const next = new Set([...this.bulkSelected].filter((pid) => !this.disabledProductIds.has(pid)));
      if (next.size !== this.bulkSelected.size) this.bulkSelected = next;
    },
    applyBulk() {
      let added = 0;
      for (const pid of this.bulkSelected) {
        if (this.disabledProductIds.has(pid)) continue;
        this.rules.push(makeRule({
          productId: pid,
          mode: this.draft.mode,
          targetProductId: this.draft.targetProductId,
          offerIdentifier: this.draft.offerIdentifier,
          message: this.cloneMessage(this.draft.message)
        }));
        added++;
      }
      notify(`Added ${added} rule${added === 1 ? '' : 's'}.`, added ? 'success' : 'info');
      this.bulkSelected = new Set();
    },
    updateRule(i, val) {
      this.$set(this.rules, i, { ...val, key: this.rules[i].key });
    },
    removeRule(i) {
      this.rules.splice(i, 1);
    },
    resolveMessage(id) {
      if (typeof id !== 'string' || !id) return null;
      const found = this.approvedMessages.find((m) => m.messageIdentifier === id || m.id === id);
      // Preserve unknown identifiers so an import never silently drops data.
      return found || { id, messageIdentifier: id, name: '(not found)', __unresolved: true };
    },
    hydrateMessage(mi) {
      if (typeof mi === 'string') {
        return { localized: false, single: this.resolveMessage(mi), entries: [] };
      }
      if (mi && typeof mi === 'object' && !Array.isArray(mi)) {
        const entries = Object.entries(mi).map(([locale, id]) => ({ locale, message: this.resolveMessage(id) }));
        return { localized: true, single: null, entries };
      }
      return { localized: false, single: null, entries: [] };
    },
    loadImport() {
      this.importError = '';
      let parsed;
      try {
        parsed = JSON.parse(this.importText);
      } catch (e) {
        this.importError = `Invalid JSON: ${e.message}`;
        return;
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        this.importError = 'Expected a JSON object keyed by productId.';
        return;
      }
      const newRules = [];
      const skipped = [];
      for (const [productId, value] of Object.entries(parsed)) {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
          skipped.push(productId);
          continue;
        }
        const mode = value.offerIdentifier && value.targetProductId
          ? 'PROMOTIONAL_OFFER'
          : value.targetProductId
            ? 'SWITCH_PLAN'
            : 'REGULAR';
        newRules.push(makeRule({
          productId,
          mode,
          targetProductId: value.targetProductId || null,
          offerIdentifier: value.offerIdentifier || null,
          message: this.hydrateMessage(value.messageIdentifier)
        }));
      }
      this.rules = newRules;
      this.importOpen = false;
      const msg = skipped.length
        ? `Loaded ${newRules.length} rule(s). Skipped ${skipped.length} non-object entr${skipped.length === 1 ? 'y' : 'ies'}.`
        : `Loaded ${newRules.length} rule(s).`;
      notify(msg, skipped.length ? 'info' : 'success');
    },
    async copyJson() {
      try {
        await navigator.clipboard.writeText(this.jsonText);
      } catch (e) {
        // Fallback for non-secure contexts / older browsers.
        const ta = document.createElement('textarea');
        ta.value = this.jsonText;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      notify('JSON copied to clipboard.', 'success');
    }
  }
};
</script>

<style scoped>
.retention-realtime {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.25rem 4rem;
  color: rgba(255, 255, 255, 0.88);
}

.page-header { margin-bottom: 1rem; }
.header-text h2 {
  margin: 0.3rem 0;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 600;
}
.hint {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  margin: 0;
  max-width: 70ch;
}
.hint code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  background: rgba(255, 255, 255, 0.06);
  padding: 1px 6px;
  border-radius: 4px;
  color: #fff;
}

.empty {
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}

/* Import */
.import-box {
  margin-bottom: 0.85rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
}
.panel-toggle {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
}
.panel-toggle:hover { color: #fff; }
.import-body { margin-top: 0.6rem; }
.import-textarea {
  width: 100%;
  min-height: 110px;
  resize: vertical;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.25);
  color: rgba(255, 255, 255, 0.92);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
  padding: 0.6rem 0.7rem;
}
.import-textarea:focus {
  outline: none;
  border-color: #03B3DA;
  box-shadow: 0 0 0 3px rgba(3, 179, 218, 0.15);
}
.import-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.5rem;
}
.import-error { color: #e74c3c; font-size: 0.82rem; }

/* Bulk add */
.bulk-box {
  margin-bottom: 0.85rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
}
.bulk-body {
  margin-top: 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}
.bulk-intro {
  margin: 0;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.55);
}
.bulk-assign {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.field-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.bulk-actions {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-top: 0.2rem;
}
.bulk-actions .btn { display: inline-flex; align-items: center; gap: 0.45rem; }
.bulk-hint { font-size: 0.8rem; color: rgba(255, 255, 255, 0.5); }

/* Builder layout */
.builder {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: 1rem;
  align-items: start;
}
.rules-col {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.unresolved-note {
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  color: #ffb84d;
}

/* JSON pane */
.json-pane {
  position: sticky;
  top: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
}
.json-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.85rem;
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.json-title {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.json-count {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: rgba(255, 255, 255, 0.45);
}
.json-body {
  margin: 0;
  padding: 0.85rem;
  overflow: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #b8e6f0;
  white-space: pre;
}

/* Buttons */
.btn {
  padding: 0.45rem 1rem;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.btn-primary { background: #03B3DA; color: #fff; }
.btn-primary:hover { background: #04a3c5; }
.btn-primary:disabled {
  background: rgba(3, 179, 218, 0.35);
  color: rgba(255, 255, 255, 0.6);
  cursor: not-allowed;
}
.btn-secondary {
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.18);
}
.btn-secondary:hover { background: rgba(255, 255, 255, 0.08); }
.btn-copy { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.35rem 0.8rem; }
.btn-add {
  align-self: flex-start;
  background: transparent;
  color: #03B3DA;
  border: 1px dashed rgba(3, 179, 218, 0.5);
}
.btn-add:hover { background: rgba(3, 179, 218, 0.1); }

/* Product checklist (bulk add) */
.bulk-pick-head {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.bulk-pick-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.bulk-search {
  position: relative;
  flex: 1;
  max-width: 320px;
}
.bulk-search .fa-search {
  position: absolute;
  left: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.78rem;
  pointer-events: none;
}
.bulk-search input {
  width: 100%;
  padding: 0.4rem 1.8rem 0.4rem 1.85rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.85rem;
}
.bulk-search input:focus {
  outline: none;
  border-color: #03B3DA;
  box-shadow: 0 0 0 3px rgba(3, 179, 218, 0.15);
}
.bulk-search .clear {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.78rem;
  cursor: pointer;
}
.bulk-search .clear:hover { color: #fff; }
.link-btn {
  background: none;
  border: none;
  color: #03B3DA;
  font-size: 0.82rem;
  cursor: pointer;
  padding: 0.2rem 0.45rem;
  border-radius: 4px;
}
.link-btn:hover { background: rgba(3, 179, 218, 0.1); }
.bulk-selcount {
  margin-left: auto;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55);
}
.product-checklist {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.15);
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1px;
}
.product-check {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.5rem 0.7rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.02);
  transition: background-color 0.12s ease;
}
.product-check:hover { background: rgba(3, 179, 218, 0.07); }
.product-check.checked { background: rgba(3, 179, 218, 0.12); }
.product-check.disabled {
  cursor: not-allowed;
  color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.01);
}
.product-check.disabled:hover { background: rgba(255, 255, 255, 0.01); }
.product-check input { accent-color: #03B3DA; cursor: inherit; flex-shrink: 0; }
.pc-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.35;
}
.pc-tag {
  margin-left: auto;
  margin-top: 1px;
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.07);
  border-radius: 999px;
  padding: 1px 7px;
  flex-shrink: 0;
}
.bulk-empty {
  grid-column: 1 / -1;
  margin: 0;
  padding: 1rem;
  text-align: center;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.45);
}

@media (max-width: 1100px) {
  .builder { grid-template-columns: 1fr; }
  .json-pane { position: static; max-height: 460px; }
}
</style>
